import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { AuthService } from "../core/services/auth.service";
import { Router, ActivatedRoute } from "@angular/router";
import { trigger, style, transition, animate } from "@angular/animations";
import { ProgramService } from "../core/services/program.service";
import { getImageUrl } from "../shared/utils/commenFunctions";
import { Subscription } from "rxjs";
import { LocationItem } from "../core/services/location.service";
import { SessionStorageService } from "../core/services/session-storage.service";
import { TokenExchangeResponse } from "../core/models/token-exchange-response";
import { LocationSelectionModalComponent } from "./location-selection-modal.component";

@Component({
  selector: "sign-in-component, SignInComponent",
  standalone: true,
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.css'],
  imports: [CommonModule, LibraryModule, LocationSelectionModalComponent],
  animations: [
    // Page load animation - only for fadeOut (route animation handles :enter)
    trigger('pageLoad', [
      transition('* => fadeOut', [
        animate('300ms ease-in-out', style({ 
          opacity: 0,
          transform: 'scale(0.9) translateZ(0)',
          pointerEvents: 'none'
        }))
      ]),
      // Prevent re-animation after fadeOut
      transition('fadeOut => *', [
        style({ 
          opacity: 0,
          transform: 'scale(0.9) translateZ(0)',
          pointerEvents: 'none'
        })
      ])
    ]),
    // Loading animation - centered scale and fade
    trigger('loadingAnimation', [
      transition(':enter', [
        style({ 
          opacity: 0,
          transform: 'scale(0.9)'
        }),
        animate('400ms ease-out', style({ 
          opacity: 1,
          transform: 'scale(1)'
        }))
      ]),
      transition(':leave', [
        animate('300ms ease-in-out', style({ 
          opacity: 0,
          transform: 'scale(0.9)'
        }))
      ])
    ])
  ]
})
export class SignInComponent implements OnInit, OnDestroy { 
  userName: string = '';
  password: string = '';
  showPassword: boolean = false;
  isSubmitting: boolean = false;
  pageState: string = '';
  isLoading: boolean = false; // Start with false to prevent double loading
  isNavigating: boolean = false; // Prevent re-rendering after successful login
  private hasPrefetchedPrograms = false;
  private readonly programImageCacheKey = 'programImageCache';
  private hasProcessedError = false; // Prevent double processing of error query param
  private isLoginInProgress: boolean = false;
  private routeSubscription?: Subscription;
  private readonly isRedirecting: boolean = false;

  showLocationModal = false;
  pendingLocations: LocationItem[] = [];
  
  // Validation errors
  userNameError: string = '';
  passwordError: string = '';
  loginError: string = '';
  
  // Date formatting
  currentDate: string = '';

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly programService: ProgramService,
    private readonly sessionStorageService: SessionStorageService
  ) {
    // Format current date: "Monday 11, December"
    const now = new Date();
    const weekday = now.toLocaleDateString('en-US', { weekday: 'long' });
    const day = now.getDate();
    const month = now.toLocaleDateString('en-US', { month: 'long' });
    this.currentDate = `${weekday} ${day}, ${month}`;
  }

  get isLoginEnabled(): boolean {
    return !this.isSubmitting;
  }

  ngOnInit(): void {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('staffInfo');
    sessionStorage.removeItem('oauthState');
    sessionStorage.removeItem('programId');
    sessionStorage.removeItem('tokenExchangeResponse');
    sessionStorage.removeItem('selectedLocation');

    // Clear session storage when user is not logged in (fresh sign-in page load)
    // Only clear if not processing Azure callback (to avoid clearing during redirect)
    const currentParams = this.route.snapshot.queryParams;
    const hash = window.location.hash;

    // Check for token in query params
    const hasTokenInParams = currentParams?.['token'] || currentParams?.['accessToken'] || currentParams?.['access_token'];
    // Check for token in hash fragment (#userInfo=...)
    const hasTokenInHash = hash?.includes('userInfo=');

    if (!hasTokenInParams && !hasTokenInHash) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('staffInfo');
      sessionStorage.removeItem('oauthState');
      sessionStorage.removeItem('programId');
      sessionStorage.removeItem('tokenExchangeResponse');
      sessionStorage.removeItem('selectedLocation');
    }

    // Check for Azure callback in URL (query params or hash fragment)
    if (hasTokenInParams || hasTokenInHash || currentParams?.['success'] === 'true') {
      // Azure callback detected, set loading state immediately to show loading indicator
      this.isLoading = true;
      // Handle it
      this.handleAzureCallback();
      return;
    }

    // Check for error query parameter from Azure OAuth callback failure
    if (currentParams?.['error']) {
      this.hasProcessedError = true;
      this.loginError = decodeURIComponent(currentParams['error']);
      // Clean up URL immediately without triggering navigation
      requestAnimationFrame(() => {
        if (window.location.search.includes('error')) {
          const url = new URL(window.location.href);
          url.searchParams.delete('error');
          window.history.replaceState({}, '', url.toString());
        }
      });
    }

    // Subscribe to route params for dynamic changes
    this.routeSubscription = this.route.queryParams.subscribe(params => {
      // Only process if not already redirecting
      if (this.isRedirecting) {
        return;
      }

      const hash = window.location.hash;
      const hasTokenInParams = params?.['token'] || params?.['accessToken'] || params?.['access_token'];
      const hasTokenInHash = hash?.includes('userInfo=');

      if (hasTokenInParams || hasTokenInHash || params?.['success'] === 'true') {
        // Azure callback detected, set loading state immediately to show loading indicator
        this.isLoading = true;
        // Handle it
        this.handleAzureCallback();
      } else if (params?.['error'] && !this.hasProcessedError) {
        this.hasProcessedError = true;
        this.loginError = decodeURIComponent(params['error']);
        // Clean up URL
        requestAnimationFrame(() => {
          if (window.location.search.includes('error')) {
            const url = new URL(window.location.href);
            url.searchParams.delete('error');
            window.history.replaceState({}, '', url.toString());
          }
        });
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

 
  onSubmit()  {
    // Clear previous errors
    this.loginError = '';
    this.isSubmitting = true;
    this.isNavigating = true;

    // Get Azure login URL and redirect to authorization page
    this.authService.getAzureLoginUrl().subscribe({
      next: (response) => {
        if (response.success && response.authorizationUrl) {
          // Store state for verification after callback
          if (response.state) {
            sessionStorage.setItem('oauthState', response.state);
          }
          // Redirect to Azure authorization URL immediately without animation
          window.location.href = response.authorizationUrl;
        } else {
          this.loginError = 'Failed to get authorization URL. Please try again.';
          this.isSubmitting = false;
          this.isNavigating = false;
          this.pageState = '';
        }
      },
      error: (error) => {
        this.loginError = error.error?.message || 'Failed to initiate login. Please try again.';
        this.isSubmitting = false;
        this.isNavigating = false;
        this.pageState = '';
      }
    });
  }

  onLocationConfirmed(location: LocationItem): void {
    this.sessionStorageService.setSelectedLocation(location);
    this.showLocationModal = false;
    this.pendingLocations = [];
    this.router.navigate(['/programs'], { replaceUrl: true });
  }

  private resolveLocationsFromTokenExchange(tokenExchangeResponse: TokenExchangeResponse): void {
    const locations = tokenExchangeResponse.locations ?? [];

    if (locations.length === 0) {
      this.isLoginInProgress = false;
      this.isLoading = false;
      this.loginError = 'No locations assigned to your account.';
      this.cleanUrl();
      return;
    }

    if (locations.length === 1) {
      this.sessionStorageService.setSelectedLocation(locations[0]);
      this.router.navigate(['/programs'], { replaceUrl: true });
      return;
    }

    this.pendingLocations = locations;
    this.showLocationModal = true;
  }

  private prefetchProgramImages(): void {
    if (this.hasPrefetchedPrograms || typeof window === 'undefined') {
      return;
    }

    this.hasPrefetchedPrograms = true;

    const programList: any[] = [
      {
        programImage: 'a347370febad46f99625eb53a4c4cef1.webp',
        image: 'a347370febad46f99625eb53a4c4cef1.webp',      },
      {
        programImage: '23f0bbcebe0848ba8e4f31ae3f5537aa.webp',
        image: '23f0bbcebe0848ba8e4f31ae3f5537aa.webp',      },
    ];
    this.cacheProgramImages(programList);
  }

  private async cacheProgramImages(programs: any[]): Promise<void> {
    if (!Array.isArray(programs) || programs.length === 0) {
      return;
    }

    const cache: Record<string, string> = {};

    await Promise.all(programs.map(async (program: any) => {
      const imageFile = program?.programImage ?? program?.image;
      if (!imageFile || cache[imageFile]) {
        return;
      }

      try {
        const response = await fetch(getImageUrl(imageFile), { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`Image fetch failed with status ${response.status}`);
        }

        const blob = await response.blob();
        const dataUrl = await this.convertBlobToDataUrl(blob);
        cache[imageFile] = dataUrl;
      } catch {
        // Image caching failed, continue with other images
      }
    }));

    sessionStorage.setItem(this.programImageCacheKey, JSON.stringify(cache));
  }

  private convertBlobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert blob to data URL'));
        }
      };
      reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
      reader.readAsDataURL(blob);
    });
  }

  private handleAzureCallback(): void {
    // Set loading state and mark login as in progress
    this.isLoading = true;
    this.isLoginInProgress = true;
    this.loginError = '';

    // Extract token from query params first (priority: token, accessToken, access_token)
    const queryParams = this.route.snapshot.queryParams;
    let accessToken = queryParams?.['token'] ?? queryParams?.['accessToken'] ?? queryParams?.['access_token'];
    let idToken: string | undefined;

    // If not in query params, try to extract from hash fragment (#userInfo={encoded_json})
    if (!accessToken) {
      const hash = window.location.hash;
      if (hash?.includes('userInfo=')) {
        try {
          // Extract userInfo from hash (format: #userInfo={encoded_json})
          const userInfoRegex = /userInfo=(.+)/;
          const userInfoMatch = userInfoRegex.exec(hash);
          if (userInfoMatch?.[1]) {
            // Decode URL-encoded JSON
            const decodedUserInfo = decodeURIComponent(userInfoMatch[1]);
            const userInfo = JSON.parse(decodedUserInfo);
            // Extract accessToken from userInfo object
            accessToken = userInfo?.accessToken;
            idToken = userInfo?.idToken;
          }
        } catch (error) {
          // Error parsing userInfo JSON
          console.error('Error parsing userInfo from hash:', error);
        }
      }
    }

    if (!accessToken) {
      // No token found in query params or hash fragment
      this.isLoginInProgress = false;
      this.isLoading = false;
      this.loginError = 'Access token not found. Please try again.';
      this.cleanUrl();
      return;
    }

    // Step 1: Verify the token with backend
    this.authService.verifyAzureToken(accessToken, idToken).subscribe({
      next: (verifyResponse: any) => {
        // Check if verify API returned success = true
        if (verifyResponse.success === true && verifyResponse.staff) {
          // Step 2: Verify succeeded, now call token exchange API
          const email = verifyResponse.staff?.email;
          if (!email) {
            this.isLoginInProgress = false;
            this.isLoading = false;
            this.loginError = 'Email not found in staff information. Please try again.';
            this.cleanUrl();
            return;
          }
          if (!idToken) {
            this.isLoginInProgress = false;
            this.isLoading = false;
            this.loginError = 'ID token not found. Please try again.';
            this.cleanUrl();
            return;
          }
          this.authService.login({
            email,
            azureAdIdToken: idToken
          }).subscribe({
            next: (tokenExchangeResponse: TokenExchangeResponse) => {
              // Store complete token exchange API response in session
              sessionStorage.setItem('tokenExchangeResponse', JSON.stringify(tokenExchangeResponse));
              // Extract and store token from exchange response (for backward compatibility)
              let token = null;
              if (tokenExchangeResponse.token) {
                token = tokenExchangeResponse.token;
              } else if ((tokenExchangeResponse as any).data?.token) {
                token = (tokenExchangeResponse as any).data.token;
              } else if ((tokenExchangeResponse as any).access_token) {
                token = (tokenExchangeResponse as any).access_token;
              }
              if (token) {
                sessionStorage.setItem('token', token);
              }
              // Store staff info in sessionStorage (from verify response)
              sessionStorage.setItem('staffInfo', JSON.stringify(verifyResponse.staff));
              // Update auth service
              this.authService.setCurrentUser(verifyResponse.staff);
              // Mark login as complete
              this.isLoginInProgress = false;
              this.isLoading = false;
              // Clean URL - remove query params
              this.cleanUrl();
              // Resolve location from token-exchange response
              this.resolveLocationsFromTokenExchange(tokenExchangeResponse);
            },
            error: (tokenExchangeError) => {
              // Token exchange API failed
              this.isLoginInProgress = false;
              this.isLoading = false;
              this.loginError = tokenExchangeError?.error?.message || 'Token exchange failed. Please try again.';
              this.cleanUrl();
            }
          });
        } else {
          // Verification failed - verify API did not return success = true
          this.isLoginInProgress = false;
          this.isLoading = false;
          this.loginError = verifyResponse?.message || 'Authentication verification failed. Please try again.';
          this.cleanUrl();
        }
      },
      error: (verifyError) => {
        // Verify API call failed
        this.isLoginInProgress = false;
        this.isLoading = false;
        this.loginError = verifyError?.error?.message || 'Authentication verification failed. Please try again.';
        this.cleanUrl();
      }
    });
  }

  private cleanUrl(): void {
    // Clean URL - remove query params and hash
    const url = new URL(window.location.href);
    url.searchParams.delete('success');
    url.searchParams.delete('error');
    url.searchParams.delete('token');
    url.searchParams.delete('accessToken');
    url.searchParams.delete('access_token');
    url.hash = '';
    window.history.replaceState({}, '', url.toString());
  }

  ngOnDestroy(): void {
    // Unsubscribe from route params to prevent memory leaks
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }
}
